import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { AuthService } from '../../services/auth/auth.service';
import { Profile } from '../../services/auth/auth.model';
import { Observable } from 'rxjs';
import { Router, ActivatedRoute } from '@angular/router';
import { mergeDeep } from 'functions/src/shared/tools';
import * as firebase from 'firebase/app';
import 'firebase/firestore';
import { BackurlService } from 'src/common/services/backurl/backurl.service';

@Component({
  selector: 'app-profile-edit',
  templateUrl: './profile-edit.component.html',
  styleUrls: ['./profile-edit.component.scss']
})
export class ProfileEditComponent implements OnInit {

  public profile = new Profile();
  public profile$: Observable<Profile>;
  public saving = false;
  public saveSuccess = false;
  public isDirector = false;
  public isAdmin = false;
  public isDirectorOrAssistant = false;
  public isUserRole = false;
  public profileLoaded = false;
  private _userId: string | null = null;

  get canSyncSignature(): boolean {
    const v = this.profile.values as any;
    return !!(v && v.agency && v.jobTitle && v.tel && v.signatureTemplate);
  }

  get lastSignatureSyncDate(): Date | null {
    const v = this.profile.values as any;
    const raw = v?.gmailSignatureSyncedAt || v?.nestennSignatureSyncedAt;

    if (!raw) {
      return null;
    }

    if (raw instanceof Date) {
      return raw;
    }

    if (typeof raw?.toDate === 'function') {
      return raw.toDate();
    }

    const parsed = new Date(raw);
    return isNaN(parsed.getTime()) ? null : parsed;
  }


  constructor(
    protected _cd: ChangeDetectorRef,
    public auth: AuthService,
    protected router: Router,
    protected route: ActivatedRoute,
    protected backURL: BackurlService,
  ) { }

  ngOnInit(): void {
    this._initProfile();
  }

  private _initProfile() {
    // decide which user to load: route param if provided, else current user
    this._userId = this.route.snapshot.paramMap.get('id');
    const currentUser = firebase.auth().currentUser;
    const targetId = this._userId || currentUser?.uid;

    if (targetId) {
      this.profile$ = this.auth.initUser(targetId);
      this.profile$.subscribe((userProfile) => {
        this.profile.fromFB(userProfile);
        this.profile.values = mergeDeep({}, this.profile.values);
        
        // Check role (roles are already lowercase)
        const role = this.auth.profile?.values?.role || '';
        this.isAdmin = role === 'admin';
        this.isDirectorOrAssistant = ['director', 'assistant'].includes(role);
        this.isDirector = this.isAdmin || this.isDirectorOrAssistant;
        this.isUserRole = !this.isAdmin && !this.isDirectorOrAssistant;
        
        this.profileLoaded = true;
        this._cd.markForCheck();
      });
    }
  }

  saveProfile() {
    this.saving = true;
    this.saveSuccess = false;

    this.auth.saveProfile(this.profile).then(() => {
      this.saving = false;
      this.saveSuccess = true;
      this._cd.markForCheck();

      // Hide success message after 3 seconds
      setTimeout(() => {
        this.saveSuccess = false;
        this._cd.markForCheck();
      }, 3000);
    }).catch(error => {
      this.saving = false;
      console.error('Error saving profile:', error);
      this._cd.markForCheck();
    });
  }

  blockUser() {
    if (this.saving) { return; }
    this.auth.blockProfile(this.profile);
  }

  activeUser() {
    if (this.saving) { return; }
    this.auth.activeProfile(this.profile);
  }

  async forceLogoutUser() {
    if (this.saving) { return; }

    const targetId = this._userId || firebase.auth().currentUser?.uid;

    if (!targetId) {
      console.warn('No user id found');
      return;
    }

    if (confirm('Are you sure you want to force this user to logout?')) {
      this.saving = true;
      this._cd.markForCheck();

      try {
        // Set the forceLogout flag to true
        await firebase.firestore().collection('users').doc(targetId).update({
          forceLogout: true,
        });
        this.saveSuccess = true;
        setTimeout(() => {
          this.saveSuccess = false;
          this._cd.markForCheck();
        }, 3000);
      } catch (error) {
        console.error('Error forcing logout:', error);
      } finally {
        this.saving = false;
        this._cd.markForCheck();
      }
    }
  }

  async syncGmailSignature() {
    if (this.saving) { return; }

    const targetId = this._userId || firebase.auth().currentUser?.uid;
    const mainAgency = this.profile.values?.agency;

    if (!targetId) {
      console.warn('No user id found');
      return;
    }

    if (!mainAgency) {
      console.warn('User does not have a main agency assigned');
      return;
    }

    this.saving = true;
    this.saveSuccess = false;
    this._cd.markForCheck();

    try {
      // Save latest profile data first
      await this.auth.saveProfile(this.profile);

      // Then request Gmail signature sync
      await firebase.firestore().collection('users').doc(targetId).collection('gmailSyncRequest').doc('sync').set({
        status: 'pending',
        requestedAt: firebase.firestore.FieldValue.serverTimestamp(),
      });

      this.saveSuccess = true;
      // Hide success after a short delay
      setTimeout(() => {
        this.saveSuccess = false;
        this._cd.markForCheck();
      }, 3000);
    } catch (error) {
      console.error('Error saving or requesting Gmail signature sync:', error);
    } finally {
      this.saving = false;
      this._cd.markForCheck();
    }
  }

  cancel() {
    this.router.navigate([this.backURL.back() || '/']);
  }
}
